<template>
  <DropdownSelect
      :label="label"
      :modelValue="modelValue"
      :options="filteredOptions"
      :maxTextLength="maxTextLength"
      :maxDropdownHeight="maxDropdownHeight"
      :searchable="true"
      @update:modelValue="handleSelectionChange"
      @search="handleSearch"
  />
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import DropdownSelect from './DropdownSelect.vue';
import reportsService from '@/service/reportsService';
import { useI18n } from 'vue-i18n'

const props = defineProps({
  label: {
    type: String,
    default: '',
  },
  modelValue: {
    type: [String, Number],
    default: 'all'
  },
  maxTextLength: {
    type: Number,
    default: 20
  },
  maxDropdownHeight: {
    type: Number,
    default: 240
  },
  dataType: {
    type: String,
    default: 'test'
  },
  options: {
    type: Array,
    default: () => []
  },
  useBackend: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits(['update:modelValue', 'optionsLoaded', 'selectionChanged']);

const backendOptions = ref([]);
const loading = ref(false);
const error = ref(null);
const searchQuery = ref('');

const apiTypeMapping = {
  test: 'selfAssessments',
  program: 'programs',
  user: 'users',
  gameLabs: 'gameLabs'
};

const computedOptions = computed(() => {
  if (!props.useBackend || props.options.length > 0) {
    return props.options;
  }

  const options = [{
    value: 'all',
    label: getAllLabel()
  }];

  backendOptions.value.forEach(item => {
    options.push({
      value: item.id,
      label: item.name
    });
  });

  return options;
});

const filteredOptions = computed(() => {
  if (!searchQuery.value) {
    return computedOptions.value;
  }

  const query = searchQuery.value.toLowerCase().trim();

  return computedOptions.value.filter(option => {
    if (option.value === 'all') {
      return true;
    }

    return option.label.toLowerCase().startsWith(query);
  });
});

const { t } = useI18n()

const getAllLabel = () => {
  const labelKeys = {
    'test': 'allTests',
    'program': 'allPrograms',
    'user': 'allUsers',
    'gameLabs': 'allGameLabs'
  };

  const key = labelKeys[props.dataType] || 'all';
  return t(key);
};

const handleSearch = (query) => {
  searchQuery.value = query;
};

const fetchBackendData = async () => {
  if (!props.useBackend) return;

  loading.value = true;
  error.value = null;

  try {
    const apiType = apiTypeMapping[props.dataType];
    if (!apiType) {
      throw new Error(`Geçersiz dataType: ${props.dataType}`);
    }

    const response = await reportsService.getReportData({
      type: apiType,
      parameterType: 'properties',
      status: 2,
      page: 1,
      limit: 100
    });

    if (response.data && response.data.data) {
      const items = response.data.data.map(item => {
        let id, name;

        switch(props.dataType) {
          case 'test':
            id = item.testId;
            name = item.name;
            break;
          case 'program':
            id = item.programId;
            name = item.name;
            break;
          case 'user':
            id = item.userId;
            name = item.fullName || item.email || `User ${item.userId}`;
            break;
          case 'gameLabs':
            id = item.gameLabsId;
            name = item.name;
            break;
          default:
            id = item.id;
            name = item.name;
        }

        return { id, name };
      });

      backendOptions.value = items;
      emit('optionsLoaded', items);
    } else {
      backendOptions.value = [];
    }

  } catch (err) {
    error.value = err.message;
    backendOptions.value = [];
  } finally {
    loading.value = false;
  }
};

const handleSelectionChange = (value) => {
  emit('update:modelValue', value);

  emit('selectionChanged', {
    value: value,
    dataType: props.dataType,
    selectedItem: value === 'all' ? null : backendOptions.value.find(item => item.id === value),
    isAll: value === 'all'
  });

  searchQuery.value = '';
};

watch(() => props.dataType, () => {
  if (props.useBackend) {
    fetchBackendData();
  }
  searchQuery.value = '';
}, { immediate: false });

onMounted(() => {
  if (props.useBackend) {
    fetchBackendData();
  }
});

defineExpose({
  fetchBackendData,
  backendOptions,
  loading,
  error,
  searchQuery
});
</script>
